import {
  ButtonIcon,
  CloseIcon,
  LinkIcon,
  AddButtonIcon,
  DeleteIcon,
} from "../../../editorjs/icons/icons";
import BtnTooltip from "../../tooltips/btnTooltip";
import { EdjsTooltips } from "../../tooltips/edjsTooltip";

/**
 * Build styles
 */
require("./index.css").toString();

export default class ButtonGroup {
  static get toolbox() {
    return {
      icon: ButtonIcon,
      title: "Button group",
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  static get sanitize() {
    return {
      br: true,
    };
  }

  constructor({ data, config, api, readOnly, block }) {
    this.api = api;
    this.readOnly = readOnly;
    this.block = block;

    this._CSS = {
      baseClass: this.api.styles.block,
      loading: this.api.styles.loader,
      settingsButton: this.api.styles.settingsButton,
      settingsButtonActive: this.api.styles.settingsButtonActive,

      /**
       * Tool's classes
       */
      btnGrpContainer: "cdx-btn-group-container",
      addBtn: "cdx-btn-group-add-btn",
      btnItemContainer: "cdx-btn-item-container",
      btnItem: "cdx-btn-item",
      urlBtn: "cdx-btn-item-url-btn",
      buttonSettings: "cdx-btn-settings-container",
      btnWidthContainer: "cdx-btn-width-container",
      btnWidthLabel: "cdx-btn-width-label",
      btnInputContainer: "cdx-btn-input-container",
      btnWidthInput: "cdx-btn-width-input",
      btnNameInput: "cdx-btn-name-input",
      urlInput: "cdx-btn-item-url-input",
      urlSaveBtn: "cdx-btn-item-url-save-btn",
      removeBtn: "cdx-btn-item-remove-btn",
      blockBtn: "block-btn",
      blockBtnSmall: "small",
      blockBtnLarge: "large",
      largeIcon: "large-icon",
      xlIcon: "xl-icon",
      xxlIcon: "xxl-icon",
      clear: "clear",
      focusVisibleBtn: "focus-visible-btn",
      tuneWrapper: "editor-block-tune",
      tuneTitle: "editor-block-tune-title",
      tuneText: "editor-block-tune-text",
      tuneInput: "editor-block-tune-input",
      editorFont: "editor-font",
      editorBlockModal: "editor-block-modal",
      editorBlockModalTitle: "editor-block-modal-title",
      editorBlockModalLabel: "editor-block-modal-label",
      editorBlockModalText: "editor-block-modal-text",
      editorBlockModalInput: "editor-block-modal-input",
      adminFont: "admin-font",
    };

    /**
     * Tool's settings passed from Editor
     *
     * @type {TitleConfig}
     * @private
     */
    this._settings = config;

    this._data = {
      buttonData: data.buttonData || [{ text: "Button text", url: "" }],
      buttonWidth:
        data.buttonWidth !== undefined
          ? data.buttonWidth
          : this._settings?.percentage
            ? "100"
            : "200",
      single: data.single || this._settings?.single || false,
      percentage: data.percentage || this._settings?.percentage || false,
    };
    this._container = "";
    this._btnGrp = document.createElement("div");
  }

  buttonElement(text, url) {
    const css = this._CSS;

    const container = document.createElement("div");
    const button = document.createElement("button");
    const urlBtn = document.createElement("button");
    const removeBtn = document.createElement("button");
    const btnSettings = this.buttonSettings(text, url, button);
    BtnTooltip(urlBtn, EdjsTooltips.buttonGroup.url);
    BtnTooltip(removeBtn, EdjsTooltips.buttonGroup.remove);

    container.classList.add(css.btnItemContainer);
    urlBtn.classList.add(
      css.urlBtn,
      css.blockBtn,
      css.blockBtnSmall,
      css.xxlIcon,
      css.focusVisibleBtn,
    );
    button.classList.add(css.btnItem);
    removeBtn.classList.add(
      css.removeBtn,
      css.blockBtn,
      css.blockBtnSmall,
      css.clear,
      css.xlIcon,
      css.focusVisibleBtn,
    );

    button.style.width = `${this._data.buttonWidth}${
      this._settings?.percentage ? "%" : "px"
    }`;

    urlBtn.innerHTML = LinkIcon;
    removeBtn.innerHTML = DeleteIcon;

    removeBtn.addEventListener("click", () => {
      container.remove();
    });

    urlBtn.addEventListener("click", () => {
      if (!btnSettings.classList.contains("show")) {
        this.hideAllSettings();
        btnSettings.classList.add("show");
      }
    });

    button.innerText = text || "Button text";

    if (!this.readOnly) {
      if (!this._settings?.single) {
        container.appendChild(removeBtn);
      }
      container.appendChild(urlBtn);
      container.appendChild(btnSettings);
    }

    container.appendChild(button);

    return container;
  }

  buttonSettings(text, url, btnElement) {
    const css = this._CSS;

    const main = document.createElement("div");
    const inputContainer = document.createElement("div");
    const btnNameInput = document.createElement("input");
    const urlInput = document.createElement("input");
    const saveBtn = document.createElement("button");

    main.classList.add(css.buttonSettings, css.editorBlockModal);
    inputContainer.classList.add(css.btnInputContainer);
    btnNameInput.classList.add(
      css.btnNameInput,
      css.editorFont,
      css.editorBlockModalInput,
    );
    urlInput.classList.add(
      css.urlInput,
      css.editorFont,
      css.editorBlockModalInput,
    );
    saveBtn.classList.add(
      css.urlSaveBtn,
      css.blockBtn,
      css.blockBtnSmall,
      css.clear,
      css.focusVisibleBtn,
    );
    btnNameInput.value = text;
    btnNameInput.placeholder = "Button text";
    urlInput.value = url;
    urlInput.placeholder = "Button URL";
    saveBtn.innerHTML = CloseIcon;

    saveBtn.addEventListener("click", () => {
      let _url = this.setUrl(urlInput.value);
      urlInput.value = _url;
      btnElement.innerText = btnNameInput.value;
      main.classList.remove("show");
    });

    inputContainer.appendChild(btnNameInput);
    inputContainer.appendChild(urlInput);
    main.appendChild(inputContainer);
    main.appendChild(saveBtn);

    return main;
  }

  renderSettings() {
    const css = this._CSS;
    const btnWidthContainer = document.createElement("div");
    const btnWidthLabel = document.createElement("label");
    const btnWidthInput = document.createElement("input");

    btnWidthContainer.classList.add(css.btnWidthContainer, css.tuneWrapper);
    btnWidthLabel.classList.add(css.btnWidthLabel, css.tuneTitle);
    btnWidthInput.classList.add(css.btnWidthInput, css.tuneInput);

    btnWidthLabel.innerText = "Button width:";
    btnWidthInput.type = "range";
    if (this._settings?.percentage) {
      btnWidthInput.min = "0";
      btnWidthInput.max = "100";
    } else {
      btnWidthInput.min = "120";
      btnWidthInput.max = "250";
    }
    btnWidthInput.value = this._data.buttonWidth;

    btnWidthInput.addEventListener("input", (e) => {
      this.modifyButtonWidth(e.target.value);
    });

    btnWidthContainer.appendChild(btnWidthLabel);
    btnWidthContainer.appendChild(btnWidthInput);

    return btnWidthContainer;
  }

  modifyButtonWidth(value) {
    let buttons = this._btnGrp.querySelectorAll(`.${this._CSS.btnItem}`);
    let sliders = this._btnGrp.querySelectorAll(`.${this._CSS.btnWidthInput}`);
    buttons.forEach((e) => {
      e.style.width = `${value}${this._settings?.percentage ? "%" : "px"}`;
    });
    sliders.forEach((e) => {
      e.value = value;
    });
    this._data.buttonWidth = value;
  }

  setUrl(value) {
    const raw = (value || "").trim();
    if (raw === "") {
      return "";
    }

    // Already an explicit scheme, or an internal (relative / anchor) link -
    // leave it exactly as typed.
    if (/^(https?:\/\/|mailto:|tel:|\/|#)/i.test(raw)) {
      return raw;
    }

    const emailPattern = /^[\w.-]+@([\w-]+\.)+[\w-]{2,4}$/;
    if (emailPattern.test(raw)) {
      return "mailto:" + raw;
    }

    // Phone: digits/formatting only (no letters), so we don't mistake a bare
    // domain like "example.com" for a number.
    const phonePattern = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.0-9]{5,}$/;
    if (phonePattern.test(raw)) {
      return "tel:" + raw;
    }

    // Anything else (bare domain / "example.com/path") is a web address -
    // default the scheme so the link is never silently dropped on save.
    return "https://" + raw;
  }

  hideAllSettings() {
    let allSettings = this._btnGrp.querySelectorAll(
      `.${this._CSS.buttonSettings}`,
    );
    allSettings.forEach((e) => {
      e.classList.remove("show");
    });
  }

  render() {
    this._btnGrp.classList.add(this._CSS.btnGrpContainer);
    const addBtn = document.createElement("button");
    BtnTooltip(addBtn, EdjsTooltips.buttonGroup.add);

    // hidden input hack
    // prevents empty blocks being created
    const hiddenInput = document.createElement("input");
    hiddenInput.setAttribute(
      "style",
      "visibility: hidden; width: 0; height: 0; padding: 0; margin: 0; border: 0; position: absolute",
    );
    this._btnGrp.appendChild(hiddenInput);

    addBtn.classList.add(
      this._CSS.addBtn,
      this._CSS.blockBtn,
      this._CSS.blockBtnSmall,
      this._CSS.xxlIcon,
      this._CSS.focusVisibleBtn,
    );
    addBtn.innerHTML = AddButtonIcon;

    if (this._data.buttonData.length > 0) {
      this._data.buttonData.forEach((e) => {
        let btnItem = this.buttonElement(e.text, e.url);
        this._btnGrp.appendChild(btnItem);
      });
    }

    if (!this._settings?.single) {
      addBtn.addEventListener("click", () => {
        let newBtn = this.buttonElement("Button text", "");
        this._btnGrp.insertBefore(newBtn, addBtn);
      });
      if (!this.readOnly) {
        this._btnGrp.appendChild(addBtn);
      }
    } else {
      this._btnGrp.classList.add("single");
    }

    return this._btnGrp;
  }

  getButtonData() {
    let buttons = this._btnGrp.querySelectorAll(
      `.${this._CSS.btnItemContainer}`,
    );
    let buttonData = [];
    buttons.forEach((e) => {
      let btnSetting = e.querySelector(`.${this._CSS.buttonSettings}`);
      let btnName = btnSetting.querySelector(`.${this._CSS.btnNameInput}`);
      let url = btnSetting.querySelector(`.${this._CSS.urlInput}`);

      buttonData.push({ text: btnName.value, url: url.value });
    });

    return buttonData;
  }

  save() {
    this._data = {
      ...this._data,
      buttonData: this.getButtonData(),
    };
    return this._data;
  }
}
